/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RavenService, type RavenTrackEvent, type TrackEventPayload } from '../raven-service';
import { RavenInsertMeta, sentryDeferred } from '@telecom/shared/startup/sentry';
import { Emitter, Logger } from '@patsnap/tahiti-core';
import { TrackerApi, type TrackerApiRequest } from '../../../infrastructure/api-functions/tracker-api';
import { UserInfoService } from '../../../domain/permission';
import { InitCookie } from '@patsnap/init-cookie';
import { initCookieService } from '@telecom/shared/utils/init-cookie.ts';
import { normalizeRavenSessionId, resolveRavenSessionId } from '../resolve-raven-session-id';

const MODULE_NAME = 'EUREKA';

function isReportSharePage(href: string): boolean {
    try {
        const url = new URL(href);
        const searchParams = [url.searchParams];
        const hashQueryIndex = url.hash.indexOf('?');
        if (hashQueryIndex >= 0) {
            searchParams.push(new URLSearchParams(url.hash.slice(hashQueryIndex + 1)));
        }
        return searchParams.some(params =>
            !!params.get('shareId')
            || params.get('task_id')?.startsWith('sh_')
            || params.get('taskId')?.startsWith('sh_'),
        );
    } catch {
        return false;
    }
}

@injectable()
export class RavenServiceImpl implements RavenService {

    private readonly _onTrackEmitter = new Emitter<RavenTrackEvent>();
    readonly onTrack = this._onTrackEmitter.event;

    constructor(
        @inject(Logger)
        private readonly logger: Logger,
        @inject(TrackerApi)
        private readonly trackerApi: TrackerApi,
        @inject(UserInfoService)
        private readonly userInfoService: UserInfoService,
    ) {}

    async track(sourceType: string, data: Partial<TrackEventPayload>): Promise<void> {
        const _eventType = data.eventType;
        if (!_eventType) {
            console.error('RavenServiceImpl.track missing eventType !');
        }
        const userLevel = await this.userInfoService.userLevel();
        const signatureId = InitCookie.getVisitorId();
        const campaignName = InitCookie.getCampaignName();
        const agent_name = initCookieService.getAgentName();
        const _sourceType = sourceType || data.sourceType || '';
        const reportSharePage = isReportSharePage(window.location.href);
        const sessionId = normalizeRavenSessionId(data.sessionId)
            || normalizeRavenSessionId(data.eventDetailStr?.session_id)
            || (reportSharePage ? undefined : resolveRavenSessionId(window.location.href));
        const trackerApiRequest:TrackerApiRequest = {
            // 默认传 'SS AI Drafting' 以兼容老数据分析
            event_type: _eventType || 'SS AI Drafting',
            source_type: _sourceType,
            other_param: data.otherParam,
            event_detail_str: this._stringify({
                // 用户的等级
                userLevel,
                // 游客版的唯一ID
                signatureId,
                // 用户第一次从哪个渠道进来的
                campaignName,
                // 产品线,有些事件其他产品线也会埋,这几加一个区分
                product_line: 'eureka_ip',
                // agent名称
                agent_name,
                // 分享页 URL 含访问凭据，普通页面继续记录地址用于行为分析。
                ...(reportSharePage ? {} : { pageUrl: window.location.href }),
                // 其他时间具体的参数
                ...(data.eventDetailStr || {}),
                // 当前 Agent 的任务或会话 ID
                ...(sessionId ? { session_id: sessionId } : {}),
            }),
            module_type: MODULE_NAME,
        };
        this._onTrackEmitter.fire({
            timestamp: Date.now(),
            request: trackerApiRequest,
        });
        if (import.meta.env.DEV) {
            this._log(_sourceType, trackerApiRequest);
            return;
        }
        await this.trackerApi.trackEvent(trackerApiRequest).catch((e) => {
            this.logger.warn(`track event failed: ${e}`);
        });
    }

    async rvTrack(eventCode: string, data: TrackEventPayload): Promise<void> {
        await sentryDeferred.promise;
        if (import.meta.env.DEV) {
            this._log(eventCode, data);
            return;
        }
        RavenInsertMeta(MODULE_NAME, eventCode, data);
    }

    private _stringify(data?: Record<string, unknown>) {
        if (!data) return undefined;
        try {
            return JSON.stringify(data);
        } catch (e) {
            return undefined;
        }
    }

    private _log(eventCode: string, data: Partial<TrackerApiRequest> | TrackEventPayload): void {
        const eventType = 'eventType' in data ? data.eventType : data.event_type;
        const logs = [
            `%cRaven%cService%c > %c${eventType}%c > %c${eventCode}`,
            'color: white;background-color: #1976D2;padding: 2px 0 2px 4px;',
            'color: white;background-color: #45A321;padding: 2px 4px 2px 0;',
            '',
            'color: white;background-color: #dc7c08;padding: 2px 4px; border-radius: 4px;',
            '',
            'color: #1976D2;font-weight: bold;',
            data,
        ];
        this.logger.info(...logs);
    }
}
